import { useQuery } from "@tanstack/react-query";
import { format, subDays, subMonths } from "date-fns";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import { officesFingerprint, officesSettled, officeDataAvailable, isAllOffices, liveOffices, useOfficeMapping, type OfficeMapping } from "./office-mapping";
import {
  REASON_CODES,
  type AgreementRateTrendPoint,
  type ExceptionsByTier,
  type ExceptionsOverridesSummary,
  type OverrideDirectionPoint,
  type OverrideRatePoint,
  type ReasonCodeCount,
  type ReasonCodeTrendSeries,
  type SegmentFilter,
  type SparkPoint,
} from "./types";

const MONTHS = 12;

function spark(points: number, start: number, end: number, jitter = 0.3): SparkPoint[] {
  return Array.from({ length: points }, (_, i) => {
    const t = points === 1 ? 1 : i / (points - 1);
    return {
      date: format(subDays(new Date(), points - 1 - i), "yyyy-MM-dd"),
      value: Number(Math.max(0, start + (end - start) * t + Math.sin(i * 1.7) * jitter).toFixed(2)),
    };
  });
}

function periods(count = MONTHS): string[] {
  return Array.from({ length: count }, (_, i) =>
    format(subMonths(new Date(), count - 1 - i), "MMM yy"),
  );
}

/** Segment weighting: PG deals are the messier book, Corp the cleaner one. */
function segmentWeight(segment: SegmentFilter) {
  if (segment === "PG") return 1.28;
  if (segment === "Corp") return 0.72;
  return 1;
}

function emptySummary(): ExceptionsOverridesSummary {
  return {
    dataAvailable: false,
    overrideRate: { current: 0, previousPeriod: 0, currentPg: 0, currentCorp: 0, sparkline: [] },
    notRatedRate: { current: 0, previousPeriod: 0, sparkline: [] },
    reconciliation: { candidateCount: 0, confirmedCount: 0, totalCount: 0 },
    overrideRateTrend: [],
    overrideDirectionTrend: [],
    // The mock fabricates a plausible override rate, so nothing is suppressed in mock mode.
    // The live endpoint fills this with the fields it cannot honestly measure.
    caveats: [],
    reasonCodeCurrentPeriod: [],
    reasonCodeTop3Trend: [],
    exceptionsByTier: [],
    agreementRateTrend: [],
  };
}

function buildOverrideRateTrend(): OverrideRatePoint[] {
  return periods().map((period, i) => {
    const t = i / (MONTHS - 1);
    return {
      period,
      overrideRatePg: Number((17.4 - 6.1 * t + Math.sin(i * 1.4) * 0.7).toFixed(1)),
      overrideRateCorp: Number((11.2 - 4.4 * t + Math.cos(i * 1.2) * 0.6).toFixed(1)),
    };
  });
}

function buildDirectionTrend(): OverrideDirectionPoint[] {
  return periods().map((period, i) => ({
    period,
    approveOverDecline: Math.round(34 - i * 1.3 + Math.sin(i * 1.9) * 4),
    declineOverApprove: Math.round(19 - i * 0.6 + Math.cos(i * 1.5) * 3),
  }));
}

/** Base current-period reason-code volumes, before segment weighting. */
const REASON_BASE: Record<string, number> = {
  "Compensating cash flow": 84,
  "Long-standing relationship": 71,
  "Strong collateral position": 58,
  "Additional guarantor support": 39,
  "Industry expertise / tenure": 24,
  "Other — analyst note": 13,
};

/** PG/Corp tilt: relationship reasons skew PG, collateral reasons skew Corp. */
const REASON_SEGMENT_TILT: Record<string, { PG: number; Corp: number }> = {
  "Compensating cash flow": { PG: 1.05, Corp: 0.95 },
  "Long-standing relationship": { PG: 1.35, Corp: 0.55 },
  "Strong collateral position": { PG: 0.72, Corp: 1.38 },
  "Additional guarantor support": { PG: 1.44, Corp: 0.38 },
  "Industry expertise / tenure": { PG: 0.98, Corp: 1.02 },
  "Other — analyst note": { PG: 1, Corp: 1 },
};

function buildReasonCodes(segment: SegmentFilter): ReasonCodeCount[] {
  const w = segmentWeight(segment);
  return REASON_CODES.map((reasonCode) => {
    const tilt =
      segment === "all" ? 1 : (REASON_SEGMENT_TILT[reasonCode]?.[segment] ?? 1);
    return {
      reasonCode,
      count: Math.max(1, Math.round((REASON_BASE[reasonCode] ?? 20) * w * tilt * 0.62)),
    };
  }).sort((a, b) => b.count - a.count);
}

/** Trend series for exactly the top 3 codes in the current-period bar chart. */
function buildTop3Trend(current: ReasonCodeCount[]): ReasonCodeTrendSeries[] {
  const labels = periods();
  return current.slice(0, 3).map((entry, seriesIndex) => ({
    reasonCode: entry.reasonCode,
    trend: labels.map((period, i) => {
      const t = i / (MONTHS - 1);
      // Series 0 climbs, series 1 is flat-ish, series 2 drifts down — the
      // "is a reason becoming more common?" question has a visible answer.
      const shape = seriesIndex === 0 ? 0.55 + 0.75 * t : seriesIndex === 1 ? 0.92 : 1.25 - 0.5 * t;
      return {
        period,
        count: Math.max(
          1,
          Math.round(entry.count * shape + Math.sin(i * 1.6 + seriesIndex) * 3),
        ),
      };
    }),
  }));
}

const TIER_BASE: { tier: ExceptionsByTier["tier"]; count: number; candidateShare: number }[] = [
  { tier: "A", count: 96, candidateShare: 0.31 },
  { tier: "B", count: 148, candidateShare: 0.37 },
  { tier: "C", count: 77, candidateShare: 0.44 },
  { tier: "DQ", count: 14, candidateShare: 0.5 },
];

function buildExceptionsByTier(segment: SegmentFilter): ExceptionsByTier[] {
  const w = segmentWeight(segment);
  return TIER_BASE.map(({ tier, count, candidateShare }) => {
    const total = Math.max(1, Math.round(count * w * 0.62));
    const candidateCount = Math.max(1, Math.round(total * candidateShare));
    return {
      tier,
      count: total,
      candidateCount,
      confirmedCount: total - candidateCount,
    };
  });
}

function buildAgreementRateTrend(): AgreementRateTrendPoint[] {
  return periods().map((period, i) => {
    const t = i / (MONTHS - 1);
    return {
      period,
      agreementRatePg: Number((79.1 + 7.4 * t + Math.sin(i * 1.3) * 0.9).toFixed(1)),
      agreementRateCorp: Number((86.4 + 5.1 * t + Math.cos(i * 1.1) * 0.8).toFixed(1)),
    };
  });
}

function generateSummary(
  offices: OfficeMapping[],
  params: { office?: string; segment: SegmentFilter },
): ExceptionsOverridesSummary {
  const office = params.office ?? "all";
  const dataAvailable = officeDataAvailable(offices, office);

  if (!dataAvailable) return emptySummary();

  const segment = params.segment;
  const overrideRateTrend = buildOverrideRateTrend();
  const latest = overrideRateTrend[overrideRateTrend.length - 1]!;
  const prior = overrideRateTrend[overrideRateTrend.length - 2] ?? latest;
  const blended = (pg: number, corp: number) => Number(((pg * 0.58 + corp * 0.42)).toFixed(1));

  const reasonCodeCurrentPeriod = buildReasonCodes(segment);
  const exceptionsByTier = buildExceptionsByTier(segment);
  // Reconciliation reflects the whole book, not the segment slice.
  const allTiers = buildExceptionsByTier("all");
  const candidateCount = allTiers.reduce((sum, t) => sum + t.candidateCount, 0);
  const confirmedCount = allTiers.reduce((sum, t) => sum + t.confirmedCount, 0);

  return {
    dataAvailable: true,
    // The mock fabricates a plausible override rate, so nothing is suppressed in mock mode.
    caveats: [],
    overrideRate: {
      current: blended(latest.overrideRatePg, latest.overrideRateCorp),
      previousPeriod: blended(prior.overrideRatePg, prior.overrideRateCorp),
      currentPg: latest.overrideRatePg,
      currentCorp: latest.overrideRateCorp,
      sparkline: spark(14, 15.1, blended(latest.overrideRatePg, latest.overrideRateCorp), 0.4),
    },
    notRatedRate: {
      current: 2.1,
      previousPeriod: 3.4,
      sparkline: spark(14, 6.8, 2.1, 0.25),
    },
    reconciliation: {
      candidateCount,
      confirmedCount,
      totalCount: candidateCount + confirmedCount,
    },
    overrideRateTrend,
    overrideDirectionTrend: buildDirectionTrend(),
    reasonCodeCurrentPeriod,
    reasonCodeTop3Trend: buildTop3Trend(reasonCodeCurrentPeriod),
    exceptionsByTier,
    agreementRateTrend: buildAgreementRateTrend(),
  };
}

export async function fetchExceptionsOverridesSummary(
  offices: OfficeMapping[],
  params: { range?: string; office?: string; compare?: boolean; segment: SegmentFilter },
) {
  if (mockFor("/exceptions-overrides/summary")) return mockResponse(generateSummary(offices, params));

  return apiGet<ExceptionsOverridesSummary>("/exceptions-overrides/summary", {
    range: params.range,
    office: params.office,
    segment: params.segment,
    compare: params.compare ? "true" : undefined,
  });
}

export function useExceptionsOverridesSummary(
  segment: SegmentFilter,
  params: { range?: string; office?: string; compare?: boolean } = {},
) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey("exceptions-overrides-summary", {
      ...params,
      segment,
      offices: officesFingerprint(offices),
    }),
    queryFn: () => fetchExceptionsOverridesSummary(offices ?? [], { ...params, segment }),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}
